import styled from 'styled-components';

// Styling for the reusable-component editor chrome (the wrapper around each
// component in the freeform editor) + the in-editor preview tile. Literal hex to
// match the rest of the self-contained debug page.

export type IssueLevel = 'error' | 'warning';

// Lightweight: a soft dashed frame at idle (reads as an editable placeholder),
// upgrading to a solid coloured border + glow only when it needs attention.
// Priority: error → warning → selected → idle. Palette: error #E32121,
// statusAmber #FFA800, brightBlue #2BBDEF (selection), #C7CDDB dashed idle.
export const Wrapper = styled.div<{ $selected?: boolean; $status?: IssueLevel | null }>`
  border: 1px ${({ $selected, $status }) => ($status || $selected ? 'solid' : 'dashed')}
    ${({ $selected, $status }) =>
      $status === 'error' ? '#e32121' : $status === 'warning' ? '#ffa800' : $selected ? '#2bbdef' : '#c7cddb'};
  border-radius: 8px;
  background: #ffffff;
  box-shadow: ${({ $selected, $status }) =>
    $status === 'error'
      ? '0 0 0 2px rgba(227, 33, 33, 0.16)'
      : $status === 'warning'
        ? '0 0 0 2px rgba(255, 168, 0, 0.18)'
        : $selected
          ? '0 0 0 2px rgba(43, 189, 239, 0.22)'
          : 'none'};
  position: relative;
  overflow: hidden;
  user-select: none;

  /* Resize grip reveals on hover (and is forced visible while dragging/selected
     via inline opacity from the NodeView). */
  &:hover .rc-resize-handle {
    opacity: 1;
  }
`;

// Right-edge drag grip for width resize (phase 2). Left-aligned full-row layout
// → only the right edge controls width.
export const ResizeHandle = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 12px;
  cursor: ew-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 120ms ease;
  touch-action: none;
  z-index: 3;

  &::after {
    content: '';
    width: 3px;
    height: 26px;
    border-radius: 2px;
    background: #2bbdef; /* brightBlue grip */
  }
`;

// Live width readout shown while dragging.
export const ResizeBadge = styled.div`
  position: absolute;
  top: 4px;
  right: 18px;
  z-index: 4;
  padding: 1px 6px;
  border-radius: 999px;
  background: #022033;
  color: #ffffff;
  font-size: 10px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  pointer-events: none;
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 6px 0;
  background: transparent;
`;

// Left cluster: drag grip + name.
export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

// Drag-to-reorder grip (carries data-drag-handle). cursor: grab → grabbing.
export const DragGrip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 22px;
  flex-shrink: 0;
  color: #9aa3b8;
  font-size: 13px;
  line-height: 1;
  border-radius: 4px;
  cursor: grab;
  &:hover {
    color: #6a7590;
    background: #f6f6f9;
  }
  &:active {
    cursor: grabbing;
  }
`;

export const Name = styled.span`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #475569;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Right-aligned cluster: status badge + action buttons.
export const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 5px;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 5px;
  font-family: inherit;
  font-size: 12px;
  color: #475569;
  cursor: pointer;
  &:hover {
    border-color: #cbd5e1;
    background: #ffffff;
  }
`;

// Compact status badge shown top-right of the wrapper when the component has
// completeness issues (the message detail is on hover; no inline text banner).
export const IssueBadge = styled.span<{ $level: IssueLevel }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  cursor: default;
  color: ${({ $level }) => ($level === 'error' ? '#991b1b' : '#92400e')};
  background: ${({ $level }) => ($level === 'error' ? '#fef2f2' : '#fffbeb')};
  border: 1px solid ${({ $level }) => ($level === 'error' ? '#fecaca' : '#fde68a')};
`;

export const Body = styled.div`
  padding: 12px;
`;

// --- in-editor preview tile (mirrors the exported stat tile) ---

export const Tile = styled.div<{ $compact?: boolean }>`
  background: #f6f6f9;
  border-radius: 8px;
  padding: ${({ $compact }) => ($compact ? '8px 10px' : '12px')};
  display: flex;
  flex-direction: ${({ $compact }) => ($compact ? 'row' : 'column')};
  align-items: ${({ $compact }) => ($compact ? 'baseline' : 'flex-start')};
  gap: ${({ $compact }) => ($compact ? '6px' : '4px')};
`;

export const TileLabel = styled.span`
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #68738f;
`;

export const TileValue = styled.span<{ $compact?: boolean }>`
  font-size: ${({ $compact }) => ($compact ? '13px' : '24px')};
  font-weight: 600;
  color: #022033;
  line-height: 1.2;
`;

export const TileCaption = styled.span`
  font-size: 8px;
  font-weight: 500;
  color: #555862;
`;
